import { signalStore, withState, withMethods, withComputed, patchState } from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { TraitesService } from './traites.service';
import { CodeMarqueItem, ContratListItem, Traite } from '../../core/models/traite.model';
import { ApiError } from '../../core/models/api-error.model';

interface TraitesState {
  codesMarque: CodeMarqueItem[];
  selectedCodeMarque: string | null;
  contrats: ContratListItem[];
  loading: boolean;
  error: ApiError | null;
  searchError: string | null;
  searchLoading: boolean;
  // Story 2.3 — fiche détail
  selectedTraite: Traite | null;
  detailLoading: boolean;
  detailError: ApiError | null;
  lockedFields: string[];
  // Story 3.1 — édition inline
  editingFields: Record<string, string>;
  fieldErrors: Record<string, string | undefined>;
  saveLoading: boolean;
  saveError: string | null;
  // Story 3.2 — création d'un nouveau traité
  createDialogOpen: boolean;
  createLoading: boolean;
  createError: string | null;
  // Story 3.3 — suppression
  deleteLoading: boolean;
  deleteError: string | null;
  // Story 3.4 — upload logo
  uploadLogoLoading: boolean;
  uploadLogoError: string | null;
  uploadingLogoField: string | null;
  // Story 7.3 — split-view
  selectedCodeInterne: string | null;
  // Mode édition
  editMode: boolean;
}

export const TraitesStore = signalStore(
  { providedIn: 'root' },
  withState<TraitesState>({
    codesMarque: [],
    selectedCodeMarque: null,
    contrats: [],
    loading: false,
    error: null,
    searchError: null,
    searchLoading: false,
    selectedTraite: null,
    detailLoading: false,
    detailError: null,
    lockedFields: [],
    editingFields: {},
    fieldErrors: {},
    saveLoading: false,
    saveError: null,
    createDialogOpen: false,
    createLoading: false,
    createError: null,
    deleteLoading: false,
    deleteError: null,
    uploadLogoLoading: false,
    uploadLogoError: null,
    uploadingLogoField: null,
    selectedCodeInterne: null,
    editMode: false,
  }),
  withComputed((store) => ({
    hasUnsavedChanges: computed(() => Object.keys(store.editingFields()).length > 0),
    hasFieldErrors: computed(() => Object.keys(store.fieldErrors()).length > 0),
  })),
  withMethods((store, service = inject(TraitesService)) => ({
    async loadCodesMarque(): Promise<void> {
      // Cache : charger une seule fois (AC1)
      if (store.codesMarque().length > 0) return;
      patchState(store, { loading: true, error: null });
      try {
        const response = await firstValueFrom(service.getCodesMarque());
        patchState(store, { codesMarque: response.items, loading: false });
      } catch (err: unknown) {
        const httpErr = err as HttpErrorResponse;
        const apiErr: ApiError = httpErr?.error ?? { code: '500', message: 'Erreur de chargement', info: '' };
        patchState(store, { loading: false, error: apiErr });
      }
    },

    async selectCodeMarque(codeMarque: string): Promise<void> {
      patchState(store, { selectedCodeMarque: codeMarque, contrats: [], loading: true, error: null });
      try {
        const response = await firstValueFrom(service.getContratsByCodeMarque(codeMarque));
        patchState(store, { contrats: response.items, loading: false });
      } catch (err: unknown) {
        const httpErr = err as HttpErrorResponse;
        const apiErr: ApiError = httpErr?.error ?? { code: '500', message: 'Erreur de chargement', info: '' };
        patchState(store, { loading: false, error: apiErr });
      }
    },

    async searchByCodeInterne(codeInterne: string): Promise<void> {
      patchState(store, { searchLoading: true, searchError: null });
      try {
        const traite = await firstValueFrom(service.getByCodeInterne(codeInterne));
        patchState(store, { searchLoading: false, selectedTraite: traite });
        this.selectTraite(codeInterne);
      } catch (err: unknown) {
        const httpErr = err as HttpErrorResponse;
        let msg = 'Erreur de chargement';
        if (httpErr?.status === 404) msg = 'Contrat introuvable';
        else if (httpErr?.status === 403) msg = 'Accès refusé à ce contrat';
        patchState(store, { searchLoading: false, searchError: msg });
      }
    },

    async loadTraiteDetail(codeInterne: string): Promise<void> {
      const cachedTraite = store.selectedTraite();
      const needsFetch = !cachedTraite || cachedTraite.code_interne !== codeInterne;
      if (needsFetch) {
        patchState(store, { detailLoading: true, detailError: null, selectedTraite: null, lockedFields: [], editingFields: {}, fieldErrors: {} });
      } else {
        patchState(store, { detailLoading: true, detailError: null });
      }
      try {
        if (needsFetch) {
          // Appel principal — bloquant
          const traite = await firstValueFrom(service.getByCodeInterne(codeInterne));
          patchState(store, { selectedTraite: traite });
        }
        // Appel secondaire — dégradation gracieuse si indisponible (AC5)
        // catchError dans le service garantit que getFieldConfig ne rejette jamais
        const config = await firstValueFrom(service.getFieldConfig('marques'));
        const fromDb = config.fields.filter(f => f.locked).map(f => f.field_name);
        // Champs immuables toujours verrouillés (indépendamment de field_config DynamoDB)
        const locked = [...new Set([...['code_interne', 'logotype_marque_defaut'], ...fromDb])];
        patchState(store, { lockedFields: locked, detailLoading: false });
      } catch (err: unknown) {
        const httpErr = err as HttpErrorResponse;
        let message = 'Erreur de chargement';
        if (httpErr?.status === 404) message = 'Contrat introuvable';
        else if (httpErr?.status === 403) message = 'Accès refusé à ce contrat';
        const apiErr: ApiError = httpErr?.error
          ? { ...httpErr.error, message }
          : { code: String(httpErr?.status ?? '500'), message, info: '' };
        patchState(store, { detailLoading: false, detailError: apiErr });
      }
    },

    /** Force un rechargement depuis le serveur en ignorant le cache (utilisé après rollback). */
    async forceReloadTraiteDetail(codeInterne: string): Promise<void> {
      patchState(store, { selectedTraite: null });
      // loadTraiteDetail verra selectedTraite=null → needsFetch=true → appel API garanti
      await this.loadTraiteDetail(codeInterne);
    },

    setFieldValue(fieldName: string, value: string): void {
      patchState(store, { editingFields: { ...store.editingFields(), [fieldName]: value } });
    },

    cancelFieldEdit(fieldName: string): void {
      const fields = { ...store.editingFields() };
      delete fields[fieldName];
      const errors = { ...store.fieldErrors() };
      delete errors[fieldName];
      patchState(store, { editingFields: fields, fieldErrors: errors });
    },

    validateField(fieldName: string, value: string): void {
      const errors: Record<string, string | undefined> = { ...store.fieldErrors() };

      // Règle FR-15 règle 2 : telephone_contact_CSC < 4 chars
      if (fieldName === 'telephone_contact_CSC' && value && value !== '#N/A' && value.length < 4) {
        errors[fieldName] = 'La valeur sera automatiquement remplacée par #N/A (< 4 caractères)';
      } else if (fieldName === 'telephone_contact_CSC') {
        delete errors[fieldName];
      }

      // Règle FR-15 règle 1 : canal non-standard → titre obligatoire
      const editingFields = store.editingFields();
      const canal = fieldName === 'canal_entrant_non-standard1' ? value : (editingFields['canal_entrant_non-standard1'] ?? '');
      const titre = fieldName === 'titre_canal_entrant_non-standard1' ? value : (editingFields['titre_canal_entrant_non-standard1'] ?? '');
      if (canal && !titre) {
        errors['titre_canal_entrant_non-standard1'] = 'Obligatoire si canal non-standard renseigné';
      } else {
        delete errors['titre_canal_entrant_non-standard1'];
      }

      patchState(store, { fieldErrors: errors });
    },

    async saveTraite(codeInterne: string): Promise<void> {
      patchState(store, { saveLoading: true, saveError: null });
      try {
        const current = store.selectedTraite();
        // M1 : n'envoyer que les champs dont la valeur a réellement changé
        const changes: Record<string, string> = {};
        for (const [key, value] of Object.entries(store.editingFields())) {
          const currentValue = String((current as Record<string, unknown>)?.[key] ?? '');
          if (value !== currentValue) {
            changes[key] = value;
          }
        }
        if (Object.keys(changes).length > 0) {
          await firstValueFrom(service.updateTraite(codeInterne, changes));
        }
        // Rafraîchir le traié affiché
        const updated = await firstValueFrom(service.getByCodeInterne(codeInterne));
        patchState(store, { selectedTraite: updated, editingFields: {}, fieldErrors: {}, saveLoading: false });
      } catch (err: unknown) {
        const httpErr = err as HttpErrorResponse;
        let message = 'Erreur de sauvegarde';
        if (httpErr?.error?.message) message = httpErr.error.message;
        else if (httpErr?.status === 502) message = 'Erreur de synchronisation Tessi — modification annulée';
        patchState(store, { saveLoading: false, saveError: message });
      }
    },

    openCreateDialog(): void {
      patchState(store, { createDialogOpen: true, createError: null });
    },

    closeCreateDialog(): void {
      patchState(store, { createDialogOpen: false, createError: null });
    },

    async deleteTraite(codeInterne: string): Promise<void> {
      patchState(store, { deleteLoading: true, deleteError: null });
      try {
        await firstValueFrom(service.deleteTraite(codeInterne));
        patchState(store, { deleteLoading: false, selectedTraite: null });
      } catch (err: unknown) {
        const httpErr = err as HttpErrorResponse;
        let message = 'Erreur de suppression';
        if (httpErr?.error?.message) message = httpErr.error.message;
        else if (httpErr?.status === 502) message = 'Erreur de synchronisation Tessi — suppression annulée';
        else if (httpErr?.status === 403) message = 'Suppression refusée.';
        else if (httpErr?.status === 404) message = 'Traité introuvable.';
        patchState(store, { deleteLoading: false, deleteError: message });
      }
    },

    async uploadLogo(codeInterne: string, file: File, logoField = 'logotype_marque_defaut'): Promise<void> {
      patchState(store, { uploadLogoLoading: true, uploadLogoError: null, uploadingLogoField: logoField });
      try {
        const result = await firstValueFrom(service.uploadLogo(codeInterne, file, logoField));
        // Recharger la fiche traité pour afficher la nouvelle URL présignée
        const updated = await firstValueFrom(service.getByCodeInterne(codeInterne));
        // Fallback : utiliser l'URL retournée par le handler si _generate_logo_url
        // échoue lors du rechargement (ex: STS temporairement indisponible)
        const finalTraite = !updated.logo_presigned_url && result.logo_presigned_url
          ? { ...updated, logo_presigned_url: result.logo_presigned_url }
          : updated;
        patchState(store, { uploadLogoLoading: false, uploadingLogoField: null, selectedTraite: finalTraite });
      } catch (err: unknown) {
        const httpErr = err as HttpErrorResponse;
        let message = "Erreur d'upload";
        if (httpErr?.error?.message) message = httpErr.error.message;
        else if (httpErr?.status === 502) message = 'Erreur de synchronisation Tessi — upload annulé';
        else if (httpErr?.status === 403) message = 'Upload refusé.';
        else if (httpErr?.status === 404) message = 'Traité introuvable.';
        patchState(store, { uploadLogoLoading: false, uploadingLogoField: null, uploadLogoError: message });
      }
    },

    selectTraite(codeInterne: string): void {
      patchState(store, { selectedCodeInterne: codeInterne, editMode: false, editingFields: {}, fieldErrors: {} });
    },

    clearSelection(): void {
      patchState(store, { selectedCodeInterne: null, selectedTraite: null, editingFields: {}, fieldErrors: {}, editMode: false });
    },

    cancelAllEdits(): void {
      patchState(store, { editingFields: {}, fieldErrors: {}, saveError: null });
    },

    enterEditMode(): void {
      patchState(store, { editMode: true });
    },

    exitEditMode(): void {
      patchState(store, { editMode: false, editingFields: {}, fieldErrors: {} });
    },

    async createTraite(data: Record<string, string>): Promise<void> {      patchState(store, { createLoading: true, createError: null });
      try {
        await firstValueFrom(service.createTraite(data));
        patchState(store, { createLoading: false, createDialogOpen: false });
        // H2-fix : navigation déléguée au TraiteNavigationComponent via dialogRef.close()
      } catch (err: unknown) {
        const httpErr = err as HttpErrorResponse;
        let message = 'Erreur de création';
        if (httpErr?.status === 409) message = 'Ce code_interne existe déjà.';
        else if (httpErr?.status === 403) message = 'Accès refusé.';
        else if (httpErr?.status === 502) message = 'Erreur de synchronisation Tessi — création annulée.';
        else if (httpErr?.error?.message) message = httpErr.error.message;
        patchState(store, { createLoading: false, createError: message });
      }
    },
  }))
);
